"use client"
import { useState, useEffect } from "react"
import { supabase } from "@/lib/supabase"
import Hero from "@/components/home/hero"
import Features from "@/components/features"
import { TestimonialsSection } from "@/components/testimonials"
import { NewReleasePromo } from "@/components/new-release-promo"
import { FAQSection } from "@/components/faq-section"
import { PricingSection } from "@/components/pricing-section"
import { StickyFooter } from "@/components/sticky-footer"
import { Button } from "@/components/ui/button"
import { Menu } from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"

const amplyLogo = new URL('../assets/amply.png', import.meta.url).href

const MusicCRMLanding = () => {
  const { user } = useAuth()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [allPlans, setAllPlans] = useState([])

  // Fallback static plans if DB not reachable
  const fallbackPlans = [
    { id: 'fallback-free', name: 'Free Plan', price_cents: 0, interval: 'month', description: 'Core features to start', features: ['Capture up to 200 fans', '1 campaign / month', 'Basic analytics', '1 smart link'] },
    { id: 'fallback-growth', name: 'Growth Plan', price_cents: 1900, interval: 'month', description: 'Scale your audience', features: ['Everything in Free', 'Up to 2,500 fans', 'Unlimited campaigns', 'Advanced analytics', 'Custom tags', '3 smart links'] },
    { id: 'fallback-pro', name: 'Pro Plan', price_cents: 4900, interval: 'month', description: 'Advanced automation', features: ['Everything in Growth', 'Up to 10,000 fans', 'Automations', 'Merch + ticketing (coming)', 'Collaboration tools', '10 smart links'] }
  ]

  useEffect(() => {
    const root = window.document.documentElement
    root.classList.remove("light", "system")
    root.classList.add("dark")
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Load plans from Supabase (similar to Settings page)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data, error } = await supabase.from('plans').select('id, name, price_cents, interval, stripe_price_id')
        if (cancelled) return
        if (error) {
          console.warn('Landing plans load error:', error.message)
          setAllPlans(fallbackPlans)
        } else if (!data || data.length === 0) {
          setAllPlans(fallbackPlans)
        } else {
          // Normalize shape expected by PricingSection (which earlier used dynamic pricing logic)
          const normalized = data.map(p => ({
            id: p.id,
            name: p.name,
            price_cents: p.price_cents,
            interval: p.interval,
            stripe_price_id: p.stripe_price_id,
          }))
          setAllPlans(normalized)
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('Landing plans fetch exception:', e)
          setAllPlans(fallbackPlans)
        }
      }
    })()
    return () => { cancelled = true }
  }, [])

  

  return (
  <div className="w-full relative text-white">
      {/* Pearl Mist Background with Top Glow */}
      <div
        className="absolute inset-0 z-0 bg-[#000000]"
        style={{
          background: "radial-gradient(ellipse 50% 35% at 50% 0%, rgba(0,0,0,0.8), rgba(255,255,255,0.1) 60%), #000",
        }}
      />

      {/* Desktop Header */}
      <header
        className={`sticky top-4 z-[9999] mx-auto hidden w-full flex-row items-center justify-between self-start rounded-full bg-transparent md:flex backdrop-blur-sm border border-[#ebcebc] shadow-lg transition-all duration-300 ${
          isScrolled ? "max-w-3xl px-2" : "max-w-5xl px-4"
        } py-2`}
        style={{
          willChange: "transform",
          transform: "translateZ(0)",
          backfaceVisibility: "hidden",
          perspective: "1000px",
        }}
      >
        <a
          className={`z-50 flex items-center justify-center gap-2 transition-all duration-300 ${
            isScrolled ? "ml-4" : ""
          }`}
          href="./"
        >
                <img 
                src={amplyLogo}
                className="w-8 h-8"
                alt="Amply Logo"
                />
        </a>

  <div className="absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-sm font-medium text-white transition duration-200 hover:text-white md:flex md:space-x-2">
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="#features"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Features</span>
          </a>
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/about"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">About</span>
          </a>
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/pricing"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Pricing</span>
          </a>
                    <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/contact"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Contact Us</span>
          </a>
        </div>

        <div className="flex items-center gap-4">
          {user ? (
            <a
              href="/dashboard"
              className="rounded-full font-bold relative cursor-pointer transition duration-300 inline-block text-center bg-[#e3c5b3] text-white shadow-lg hover:shadow-xl hover:scale-105 px-6 py-3 text-base"
            >
              Dashboard
            </a>
          ) : (
            <>
              <a
                href="/login"
                className="rounded-full border border-[#e3c5b3] font-bold relative cursor-pointer transition duration-200 inline-block text-center text-primary-foreground px-4 py-2 text-sm"
              >
                Log In
              </a>

              <a
                href="/register"
                className="rounded-full font-bold relative cursor-pointer transition duration-300 inline-block text-center bg-[#e3c5b3] text-white shadow-lg hover:shadow-xl hover:scale-105 px-6 py-3 text-base"
              >
                Sign Up
              </a>
            </>
          )}
        </div>
      </header>

      {/* Mobile Header */}
  <header className="sticky top-4 z-[9999] mx-4 flex w-auto flex-row items-center justify-between rounded-full bg-transparent backdrop-blur-sm border border-[#ebcebc] shadow-lg md:hidden px-4 py-3">
        <a
          className="flex items-center justify-center gap-2"
          href="/"
        >
                <img 
                src={amplyLogo}
                className="w-8 h-8"
                alt="Amply Logo"
                />
        </a>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-background/50 border border-border/50 transition-colors hover:bg-background/80"
          aria-label="Toggle menu"
        >
          <div className="flex flex-col items-center justify-center w-5 h-5 space-y-1">
            {isMobileMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
             <Menu className="w-5 h-5 text-foreground" />
            )}
          </div>
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
  <div className="fixed inset-0 z-[9998] bg-transparent backdrop-blur-sm md:hidden">
    <div className="absolute top-28 left-4 right-4 bg-transparent/80 backdrop-blur-md border border-border/50 rounded-2xl shadow-2xl p-6">
      <nav className="flex flex-col space-y-4">
        <a
            href="#features"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          Features
        </a>
        <a
          href="/about"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          About
        </a>
        <a
          href="/pricing"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          Pricing
        </a>
        <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/contact"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Contact Us</span>
          </a>
        <div className="border-t border-border/50 pt-4 mt-4 flex flex-col space-y-3">
          {user ? (
            <a
              href="/dashboard"
              className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
            >
              Dashboard
            </a>
          ) : (
            <>
              <a
                href="/login"
                className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
              >
                Log In
              </a>
              <a
                href="/register"
                className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
              >
                Sign Up
              </a>
            </>
          )}
        </div>
      </nav>
    </div>
  </div>
)}

      {/* Hero Section */}
      <Hero />

      {/* Features Section */}
      <div id="features">
        <Features />
      </div>

      {/* Pricing Section */}
      <div id="pricing">
        <PricingSection plans={allPlans} />
      </div>


      <NewReleasePromo />
      <div className="h-[500px]"/>
      <StickyFooter />
    </div>
  )
}

export default MusicCRMLanding;
